import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
} from '@angular/core';
import { keyBy } from 'lodash';

import {
  DoodProductModel,
  IProductAdditionGroupInformation,
  ISelectedCompoundItem,
} from '@core/models/product.model';
import { IAdditionGroup } from '@core/models/shop.model';

import { ProductKeys } from '@config/keys/product.keys';
import { AdditionGroupKeys } from '@config/keys/shop.keys';

import { ProductTypeValues } from '@config/values/product.values';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ProductService } from '@core/services/product/product.service';
import { BackHeaderAtomComponent } from '../back-header-atom/back-header-atom.component';
import { ModalFillerAtomComponent } from '../modal-filler-atom/modal-filler-atom.component';
import { CounterAtomComponent } from '../counter-atom/counter-atom.component';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { PriceAtomComponent } from '../price-atom/price-atom.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonAtomComponent } from '../button-atom/button-atom.component';
import { AutoFillCartItemRulesAdditionsGroup } from '@shared/local-store/cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';
import { CatalogFilterMatchTagsComponent } from '@shared/blocks/shop-product-list-block/catalog-filter-match-tags.component';

@Component({
  selector: 'app-compound-item-panel-atom',
  templateUrl: './compound-item-panel-atom.component.html',
  styleUrls: ['./compound-item-panel-atom.component.scss'],
  imports: [
    ContentBuilderColorPipe,
    BackHeaderAtomComponent,
    ModalFillerAtomComponent,
    CounterAtomComponent,
    PriceAtomComponent,
    TranslatePipe,
    ButtonAtomComponent,
    CatalogFilterMatchTagsComponent,
  ],
})
export class CompoundItemPanelAtomComponent implements OnInit {
  private readonly productService = inject(ProductService);

  products = input.required<IProductAdditionGroupInformation[]>();
  title = input.required<string>();
  count = input.required<number>();
  minCount = input.required<number>();
  selected = input.required<ISelectedCompoundItem[]>();
  parentIdOfAdditions = input<string>();
  isAdditionPanel = input<boolean>(false);
  isMobile = input<boolean>(false);
  groupId = input.required<string>();
  index = input.required<number>();
  storeId = input.required<string>();
  headingColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  descriptionColor = input<IContentBuilderFieldColor>({
    value: 'neutral-700',
    type: ColorFieldTypesValues.Palette,
  });
  inactiveStateColor = input<IContentBuilderFieldColor>({
    value: 'neutral-500',
    type: ColorFieldTypesValues.Palette,
  });
  priceColor = input<IContentBuilderFieldColor>({
    value: 'neutral-700',
    type: ColorFieldTypesValues.Palette,
  });
  backgroundColor = input<IContentBuilderFieldColor | undefined>();
  hideAdditionGroupDetails = input<boolean>(false);

  @Output() openAdditionsPanel = new EventEmitter<{
    productId: string;
    groupId: string;
  }>();

  @Output() updateProducts = new EventEmitter<{
    products: string[];
    parent: string;
    groupId?: string;
  }>();

  @Output() outStoreId = new EventEmitter<string>();

  @Output() closeModal = new EventEmitter<Event>();

  selectedProducts: string[] = [];

  /** Missing selection must be read as an empty list, never as `.length` on undefined. */
  private selectionOrEmpty(): string[] {
    return this.selectedProducts ?? [];
  }

  productKeys = ProductKeys;

  productTypeValues = ProductTypeValues;

  // Keep the products in the order received from the API.
  protected readonly orderedProducts = computed(() => this.products());

  protected isAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }

  protected isExclusiveAdditionGroup(group: IAdditionGroup): boolean {
    return AutoFillCartItemRulesAdditionsGroup.hasExclusiveAdditionInAdditionsGroup({
      additionGroup: group,
    });
  }

  protected visibleAdditionGroups(product: IProductAdditionGroupInformation): IAdditionGroup[] {
    return (
      product.additions_group_informations?.filter(
        group => !!group.items?.length && !this.isExclusiveAdditionGroup(group),
      ) ?? []
    );
  }

  constructor() {
    // Effect to handle selected changes similar to ngOnChanges
    effect(() => {
      const selected = this.selected();
      const isAdditionPanel = this.isAdditionPanel();

      if (!isAdditionPanel) {
        this.selectedProducts = selected ? selected.map(el => el.id) : [];
      }
    });
  }

  ngOnInit(): void {
    if (this.isAdditionPanel()) {
      // After exclusive auto-fill, additions_by_group only contains exclusive groups.
      // A non-exclusive panel then has no `selected` yet — keep an empty list instead of undefined.
      this.selectedProducts =
        this.selected()
          ?.find(el => el.id === this.parentIdOfAdditions())
          ?.additions_by_group?.find(el => el[AdditionGroupKeys.Id] === this.groupId())?.selected ??
        [];
    } else {
      this.selectedProducts = this.selected() ? this.selected().map(el => el.id) : [];
    }
  }

  toggleCheckbox(event: any): void {
    const productsIndexed = keyBy(this.products(), 'id');
    const toggledProduct = productsIndexed[event.target.id];
    if (!toggledProduct || !this.isAvailable(toggledProduct)) {
      event.target.checked = false;
      return;
    }
    if (this.isAdditionPanel() && !this.count()) {
      if (event.target.checked) {
        if (this.selectedProducts?.length) {
          this.selectedProducts = [...this.selectedProducts, event.target.id];
          if (toggledProduct.min_count && toggledProduct.min_count > 1) {
            for (let i = 1; i < toggledProduct.min_count; i++) {
              this.selectedProducts.push(event.target.id);
            }
          }
        } else {
          this.selectedProducts = [event.target.id];
          if (toggledProduct.min_count && toggledProduct.min_count > 1) {
            for (let i = 1; i < toggledProduct.min_count; i++) {
              this.selectedProducts.push(event.target.id);
            }
          }
        }
      } else {
        this.selectedProducts = this.selectionOrEmpty().filter(el => el !== event.target.id);
      }
    } else {
      if (event.target.checked) {
        if (
          this.selectedProducts?.length &&
          this.count() !== null &&
          this.count() <= this.selectedProducts?.length &&
          this.count() < this.products().length
        ) {
          this.selectedProducts = [...this.selectedProducts, event.target.id];
          this.selectedProducts.shift();
        } else if (this.selectedProducts?.length) {
          if (
            this.selectedProducts?.length < this.count() ||
            (this.selectedProducts?.length && this.count() === null)
          ) {
            this.selectedProducts = [...this.selectedProducts, event.target.id];
          } else {
            event.target.checked = false;
          }
        } else {
          this.selectedProducts = [event.target.id];
        }
      } else {
        const selection = this.selectionOrEmpty();
        if (selection.length === 1) {
          this.selectedProducts = [];
        } else {
          this.selectedProducts = selection.filter(productId => productId !== event.target.id);
        }
      }
    }

    this.updateProducts.emit({
      products: this.selectedProducts,
      parent: this.parentIdOfAdditions() as string,
      groupId: this.groupId(),
    });
  }

  openAdditions(product: DoodProductModel, groupId: string): void {
    if (!this.isAvailable(product)) {
      return;
    }
    const selection = this.selectionOrEmpty();
    if (!selection.includes(product[ProductKeys.Id])) {
      if (
        selection.length &&
        this.count() <= selection.length &&
        this.count() < this.products().length
      ) {
        this.selectedProducts = [...selection, product[ProductKeys.Id]];

        this.selectedProducts.shift();
        this.updateProducts.emit({
          products: [...this.selectedProducts],
          parent: this.parentIdOfAdditions() as string,
          groupId: this.groupId(),
        });
      } else {
        this.updateProducts.emit({
          products: [...selection, product[ProductKeys.Id]],
          parent: this.parentIdOfAdditions() as string,
          groupId: this.groupId(),
        });
      }
    }
    this.openAdditionsPanel.emit({
      productId: product[ProductKeys.Id],
      groupId,
    });
  }

  isChecked(id: string): boolean {
    return this.selectionOrEmpty().some(product => product === id);
  }

  checkIfGroupIsNotValid(): boolean {
    let isNotValid = false;
    if (
      (this.minCount() > 0 && !this.selected()?.length) ||
      this.selected()?.length < this.minCount() ||
      (this.count() > 0 && this.count() !== null && this.selected()?.length > this.count())
    ) {
      isNotValid = true;
    }
    this.products().forEach(product => {
      this.selected()?.forEach(el => {
        if (product[ProductKeys.Id] === el.id) {
          product.additions_group_informations?.map(group => {
            if (group[AdditionGroupKeys.MinCount] > 0 && !group.isValid) {
              isNotValid = true;
            }
          });
        }
      });
    });

    return isNotValid;
  }

  calculateNbProduct(product: string): number {
    if (!this.isAdditionPanel()) {
      const selected = this.selected().find(el => el.id === this.parentIdOfAdditions())?.additions;
      if (selected) {
        return selected ? selected.filter(el => el === product).length : 1;
      }

      return this.selected().filter(el => el.id === product).length;
    } else {
      const additionsByGroup = this.selected().find(
        el => el.id === this.parentIdOfAdditions(),
      )?.additions_by_group;
      if (Array.isArray(additionsByGroup)) {
        const selectedAdditions = additionsByGroup?.find(
          el => el[AdditionGroupKeys.Id] === this.groupId(),
        )?.selected;
        return selectedAdditions?.filter(el => el === product).length
          ? selectedAdditions?.filter(el => el === product).length
          : 1;
      } else {
        if (additionsByGroup) {
          return (additionsByGroup as IAdditionGroup).selected?.filter(el => el === product).length
            ? ((additionsByGroup as IAdditionGroup).selected?.filter(el => el === product)
                .length as number)
            : 1;
        }
      }
      return 1;
    }
  }

  changeNbProduct(value: number, product: string): void {
    if (!this.isAdditionPanel()) {
      this.changeNbOfProduct(value, product);
    } else {
      this.changeNbProductOfAdditions(value, product);
    }
  }

  onValidate(): void {
    document.getElementById('container')?.scrollTo(0, 0);
    this.outStoreId.emit(this.storeId());
  }

  private changeNbOfProduct(value: number, product: string): string[] {
    const selection = this.selectionOrEmpty();
    let duplicate = selection.filter(el => el === product).length;

    let copyOfSelectedProducts: string[] = [];
    selection.forEach(el => {
      if (el === product && duplicate < value) {
        copyOfSelectedProducts = Object.assign([], selection);
        copyOfSelectedProducts.push(product);
        duplicate = duplicate + 1;
      } else if (el === product && duplicate > value) {
        const index = selection.indexOf(product);
        if (index > -1) {
          copyOfSelectedProducts = Object.assign([], selection);
          copyOfSelectedProducts.splice(index, 1);
        }
        duplicate = duplicate - 1;
      }
    });
    this.updateProducts.emit({
      products: copyOfSelectedProducts,
      parent: this.parentIdOfAdditions() as string,
    });
    return copyOfSelectedProducts;
  }

  private changeNbProductOfAdditions(value: number, product: string): string[] {
    const selection = this.selectionOrEmpty();
    const countOccurrences = selection.filter(el => el === product).length;
    let copyOfSelectedProducts: string[] = [];
    if (countOccurrences < value) {
      copyOfSelectedProducts = Object.assign([], selection);
      copyOfSelectedProducts.push(product);
    } else if (countOccurrences > value) {
      const index = selection.findIndex(el => el === product);
      copyOfSelectedProducts = Object.assign([], selection);
      copyOfSelectedProducts.splice(index, 1);
    }
    this.selectedProducts = copyOfSelectedProducts;
    this.updateProducts.emit({
      products: copyOfSelectedProducts,
      parent: this.parentIdOfAdditions() as string,
      groupId: this.groupId(),
    });
    return this.selectedProducts;
  }

  forceType(data: any): any {
    return data as any;
  }
}
